import type { EligibleApproverCandidates } from 'src/hooks/queries/approval/useEligibleApproverCandidates';

/**
 * The approver picker's user/group filters (COI-571).
 *
 * Pure, and separated from the component, because this is the rule that decides
 * who can be given approval authority — it deserves to be assertable without a
 * DOM.
 *
 * `eligibleApprovers` is the server's answer, or `null` when there is no answer
 * to use: the `trpc` flag is off, the query is still loading or errored, or no
 * workflow has been chosen yet. `null` means "fall back", NEVER "nobody is
 * eligible" — rendering an empty picker from a non-answer is precisely the
 * silent fail-closed this ticket removes.
 */
export const buildApproverFilters = ({
  ancestorContributors,
  eligibleApprovers,
  trpcEnabled,
}: {
  ancestorContributors: {
    UserGroupId?: null | string;
    UserId?: null | string;
  }[];
  eligibleApprovers: EligibleApproverCandidates | null;
  trpcEnabled: boolean;
}) => {
  const contributorUserIds = ancestorContributors.map((c) => c.UserId);
  const contributorGroupIds = ancestorContributors.map((c) => c.UserGroupId);

  const userFilter = (user: {
    Id?: null | string | undefined;
    RoleKey?: null | string | undefined;
  }): boolean => {
    // V3 — the server has answered the whole rule (the
    // `${resourceType}_approval` Manager companion OR owner/contributor of the
    // parent object). No `RoleKey` comparison happens on this path: that column
    // is the retired V2 one, frozen at provisioning, so on V3 it matched nobody
    // and the picker silently offered no administrators at all.
    if (eligibleApprovers) {
      return !!user.Id && eligibleApprovers.userIds.has(user.Id);
    }

    // No answer. The V2 `RoleKey` arm survives here and ONLY here, so the
    // flag-off path behaves exactly as before. With the flag on but no answer
    // yet we fall back to owner/contributor alone — narrower than the eventual
    // answer, so we never briefly offer someone the server will reject, and
    // never offer an empty list where owners exist.
    return (
      (!trpcEnabled && user.RoleKey === 'RiskManager') ||
      contributorUserIds.includes(user.Id)
    );
  };

  const groupFilter = (group: { Id: string; OwnerContributor: boolean }) => {
    // A set constrains; `null` means the server imposes no group rule, because
    // a global approval has no parent object for one to apply to. That mirrors
    // the legacy client, which applied its group filter only when parented.
    if (eligibleApprovers) {
      return (
        eligibleApprovers.userGroupIds === null ||
        eligibleApprovers.userGroupIds.has(group.Id)
      );
    }

    return group.OwnerContributor && contributorGroupIds.includes(group.Id);
  };

  return { groupFilter, userFilter };
};
